<template>
  <GlPageWrap>
    <div class="profile-wrapper">
      <CardContainer class="mt-4">
        <CardHeader
          font-size="24"
          title="Change Plan"
        >
          <template #back>
            <gl-icon
              class="pointer"
              :height="24"
              name="report-back-arrow"
              :width="24"
              @click="goBack"
            />
          </template>
        </CardHeader>

        <Section
          v-if="hasActiveSubscription && currentPlanDisplay"
          class="mb-4"
          label="your Current Plan"
        >
          <PlanCard
            v-bind="currentPlanDisplay"
            current
            hide-action
          />
        </Section>

        <div class="flex align-center justify-center mt-3 mb-4">
          <GlSelectButton
            gray-padding="wide"
            grey
            :options="computedPlanTypes"
            :value="activeType"
            @input="typeChange"
          />
        </div>

        <template v-for="group in planGroups">
          <Section
            v-if="group.items.length"
            :key="group.id"
            :label="group.label"
          >
            <PlanCard
              v-for="plan in group.items"
              :key="`${plan.id}-${group.id}`"
              v-bind="plan"
              :action-label="actionLabel"
              class="mb-4"
              :class="group.cardClass"
              :loading="loadingOnCancelDowngrade"
              :safe-plan="currentPlan"
              :selected-type="selectedCycleValue"
              :variant="group.variant"
              @action="initiatePlanAction"
            />
          </Section>
        </template>
      </CardContainer>
    </div>

    <ConfirmPlanActionModal
      v-if="selectedActionData"
      v-model="showConfirmModal"
      :action-status="selectedActionData.actionStatus"
      :active-type-value="activeType?.value"
      :current-plan="currentPlan"
      :loading="isLoading"
      :plan="selectedActionData.plan"
      @close="onCloseConfirmModal"
      @confirm="executePlanAction"
    />
  </GlPageWrap>
</template>

<script>
// Vuex
import { mapState, mapGetters, mapActions } from 'vuex';

// Components
import GlIcon from "@/components/gl-icon.vue";
import Section from "@/pages/profile/account/Section.vue";
import GlPageWrap from "@/components/layout/gl-page-wrap.vue";
import PlanCard from "@/pages/profile/components/PlanCard.vue";
import GlSelectButton from "@/components/gl-select-button2.vue";
import CardHeader from "@/pages/profile/account/CardHeader.vue";
import CardContainer from "@/pages/profile/account/CardContainer.vue";
import StripeModal2 from "@/pages/payment/3rd-party/stripe/StripeModal2.vue";
import ConfirmPlanActionModal from "@/pages/profile/modals/ConfirmPlanActionModal.vue";

// Utils
import { createPlansList, formatPlanData, billingCycle, getAnnualSaveDiff, getPlanOptionsWithProfit } from '@/utils/plans';

// Constants
const TIER_WEIGHTS = { free: 0, starter: 1, standard: 2, pro: 3 };

export default {
  name: 'ChangePlanPage',
  components: {
    GlSelectButton,
    Section,
    PlanCard,
    GlIcon,
    CardHeader,
    CardContainer,
    GlPageWrap,
    ConfirmPlanActionModal
  },
  data: () => ({
    isLoading: false,
    processingPlanId: null,
    activeType: null,
    showConfirmModal: false,
    selectedActionData: null,
    loadingOnCancelDowngrade: false,
  }),
  computed: {
    ...mapState('payment', ['currentPlan']),
    ...mapGetters('payment', ['getPlans']),

    selectedCycleValue() {
      return this.activeType?.value || 'annual';
    },

    hasActiveSubscription() {
      const { status, planTier } = this.currentPlan || {};
      return status !== 'none' && planTier !== 'free';
    },

    actionLabel() {
      return !this.hasActiveSubscription ? 'Get Started' : '';
    },

    computedPlanTypes() {
      if (!this.hasActiveSubscription) return billingCycle;
      const diff = getAnnualSaveDiff(this.getPlans || []);
      return getPlanOptionsWithProfit(billingCycle, diff);
    },

    preparedPlans() {
      const rawPlans = this.getPlans || [];
      if (!rawPlans.length) return [];

      return createPlansList({ plans: rawPlans }).map(plan => formatPlanData({
        plan,
        currentPlan: this.currentPlan,
        selectedCycle: this.selectedCycleValue,
        tierWeights: TIER_WEIGHTS,
        isLoading: this.isLoading,
        processingPlanId: this.processingPlanId
      }));
    },

    currentPlanDisplay() {
      if (!this.hasActiveSubscription) return null;

      const rawPlans = this.getPlans || [];
      const plan = createPlansList({ plans: rawPlans })
          .find(p => p.tier === this.currentPlan?.planTier);

      if (!plan) return null;

      return formatPlanData({
        plan,
        currentPlan: this.currentPlan,
        selectedCycle: this.currentPlan?.billingCycle === 'annual' ? 'annual' : 'month',
        tierWeights: TIER_WEIGHTS
      });
    },

    planGroups() {
      return [
        {
          id: 'upgrade',
          label: 'Upgrade Options',
          variant: 'primary',
          cardClass: 'plan-card--popular-mask gradient-animation',
          items: this.preparedPlans.filter(p =>
              p.actionStatus === 'upgrade' ||
              (p.actionStatus === 'switch-cycle' && this.selectedCycleValue === 'annual')
          )
        },
        {
          id: 'downgrade',
          label: 'Downgrade Options',
          variant: 'secondary',
          cardClass: '',
          items: this.preparedPlans.filter(p =>
              p.actionStatus === 'downgrade' ||
              (p.actionStatus === 'switch-cycle' && this.selectedCycleValue === 'month')
          )
        }
      ];
    }
  },
  async mounted() {
    this.$root.$on('payment-modal:close', this.handleClosePayment)

    await this.fetchCurrentPlan();
    this.initActiveType();
    this.handleUrlParams();
  },
  beforeDestroy() {
    this.$root.$off('payment-modal:close', this.handleClosePayment)
  },
  methods: {
    ...mapActions('payment', ['changePlan', 'fetchCurrentPlan', 'createCheckout', 'cancelDowngrade']),
    ...mapActions('modal', ['openModal']),

    handleClosePayment() {
      this.$router.replace({
        path: this.$route.path
      })
    },
    initActiveType() {
      const cycle = this.currentPlan?.billingCycle;
      this.activeType = this.computedPlanTypes.find(t => t.value === cycle) || this.computedPlanTypes[0];
    },

    handleUrlParams() {
      const { planTier, isTrialRequest, actionStatus } = this.$route.query;
      let { billingCycle } = this.$route.query;

      if (billingCycle === 'monthly') billingCycle = 'month';

      if (!planTier || !billingCycle) return;

      const targetCycleOption = this.computedPlanTypes.find(t => t.value === billingCycle);

      if (!targetCycleOption) return;

      this.activeType = targetCycleOption;

      const planExists = (this.getPlans || []).some(p => p.tier === planTier);

      if (!planExists) return;

      // if (this.hasActiveSubscription) {
      //   this.$router.replace({ path: this.$route.path, query: {} });
      //   return;
      // }

      this.initiatePlanAction({
        id: planTier,
        status: actionStatus || 'upgrade',
        startTrial: isTrialRequest === 'true'
      });
    },

    goBack() {
      this.$router.push({ name: 'profile' });
    },

    typeChange(type) {
      this.activeType = type;
    },

    initiatePlanAction(emittedData) {
      if (emittedData.status === 'cancel') {
        this.handleCancelDowngrade()
        return;
      }

      const fullPlanData = this.preparedPlans.find(p => p.id === emittedData.id);

      if (!fullPlanData) return;

      if (emittedData.startTrial) {
        fullPlanData.startTrial = emittedData.startTrial;
      }

      this.selectedActionData = {
        plan: fullPlanData,
        actionStatus: emittedData.status
      };

      // if (!this.hasActiveSubscription) {
      //   this.executePlanAction();
      //   return;
      // }

      this.showConfirmModal = true;
    },

    async handleCancelDowngrade() {
      this.loadingOnCancelDowngrade = true;
      try {
        await this.cancelDowngrade();
      } catch (error) {
        console.error(error);
      } finally {
        await this.fetchCurrentPlan();
        this.loadingOnCancelDowngrade = false;
      }
    },

    onCloseConfirmModal() {
      this.clearConfirmModal();
      this.$router.replace({ path: this.$route.path, query: {} });
    },
    clearConfirmModal() {
      this.showConfirmModal = false;
      this.selectedActionData = null;
    },

    async executePlanAction(emittedCycleValue) {
      if (!this.selectedActionData) return;

      const fullPlanData = this.selectedActionData.plan;

      this.isLoading = true;
      this.processingPlanId = fullPlanData.id;

      const finalCycleValue = emittedCycleValue
          ? emittedCycleValue
          : this.selectedCycleValue;

      try {
        const returnUrl = window.location.href + (window.location.search ? '&' : '?') + 'payment_success=change-plan';
        const payload = {
          planTier: fullPlanData.id,
          targetTier: fullPlanData.id,
          billingCycle: finalCycleValue,
          targetCycle: finalCycleValue,
          paymentMethod: 'card',
          returnUrl,
        };

        if (!this.hasActiveSubscription && typeof fullPlanData.startTrial === 'boolean') {
          payload.startTrial = fullPlanData.startTrial;
        }

        const response = await (this.hasActiveSubscription
            ? this.changePlan(payload)
            : this.createCheckout(payload));

        if (response?.success) {
          this.clearConfirmModal()
          await this.handleSuccessResponse(response, fullPlanData);
        }

      } catch (error) {
        this.notifyError(error.message);
      } finally {
        this.isLoading = false;
        this.processingPlanId = null;
      }
    },

    async handleSuccessResponse(response, planData) {
      const { clientSecret, redirectUrl, isSetupIntent } = response.data;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      if (clientSecret) {
        await this.openModal({
          component: StripeModal2,
          props: {
            packageData: { ...planData, _id: planData.id },
            closable: true,
            insertClientSecret: clientSecret,
            isTrial: isSetupIntent ?? !!planData.startTrial
          }
        });
      }

      await this.fetchCurrentPlan();
    },

    notifyError(detail) {
      this.$toast.add({
        severity: 'error',
        summary: 'Error',
        detail: detail || 'An error occurred',
        life: 5000
      });
    },
  }
};
</script>

<style scoped>
.profile-wrapper {
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding-top: var(--header-height);
  background-color: var(--light-grey-f-8);
}
</style>
